import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useBlog } from '../context/BlogContext';
import { PageWrapper, Container } from '../styles/shared';

const BlogContainer = styled.main`
  background: #0e1217;
  color: white;
  min-height: 100vh;
  width: 100%;
  padding-top: 64px;
`;

const BlogHeader = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  text-align: center;

  h1 {
    font-size: 3rem;
    color: #28c0ef;
    margin-bottom: 1rem;
  }

  p {
    color: rgba(255, 255, 255, 0.8);
    font-size: 1.2rem;
  }
`;

const SearchBar = styled.div`
  max-width: 600px;
  margin: 0 auto 2rem;
  padding: 0 2rem;
  
  input {
    width: 100%;
    padding: 1rem 1.5rem;
    background: #161b22;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    color: white;
    font-size: 1.1rem;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: #28c0ef;
      box-shadow: 0 0 0 2px rgba(40, 192, 239, 0.2);
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4);
    }
  }
`;

const BlogGrid = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
`;

const BlogCard = styled(motion.div)`
  background: #161b22;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: transform 0.2s;

  &:hover {
    transform: translateY(-5px);
  }

  .image-container {
    position: relative;
    width: 100%;
    height: 200px;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: linear-gradient(to bottom, rgba(14, 18, 23, 0), rgba(14, 18, 23, 0.8));
      z-index: 1;
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .overlay-content {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      padding: 2rem;
      z-index: 2;
      color: white;

      h2 {
        font-size: 1.8rem;
        color: white;
        margin-bottom: 0.5rem;
      }

      .caption {
        color: rgba(255, 255, 255, 0.9);
        font-size: 1.1rem;
      }
    }
  }

  .content {
    padding: 1.5rem;

    p {
      color: rgba(255, 255, 255, 0.8);
      margin-bottom: 1rem;
      line-height: 1.6;
    }

    .meta {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.9rem;
    }
  }
`;

const BlogPage: React.FC = () => {
  const navigate = useNavigate();
  const { searchTerm, setSearchTerm } = useBlog();

  const handleBlogClick = (slug: string) => {
    navigate(`/blog/${slug}`);
  };

  // Empty array for future blog posts
  const blogs: Array<{
    slug: string;
    title: string;
    caption: string;
    date: string;
    readTime: string;
    excerpt: string;
  }> = [];

  const filteredBlogs = blogs.filter(blog => 
    blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    blog.caption.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <PageWrapper>
      <BlogContainer>
        <BlogHeader>
          <h1>SparkSkool Blog</h1>
          <p>Insights, updates, and stories from our team</p>
        </BlogHeader>

        <SearchBar>
          <input
            type="text"
            placeholder="Search articles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchBar>

        {filteredBlogs.length > 0 ? (
          <BlogGrid>
            {filteredBlogs.map(blog => (
              <BlogCard
                key={blog.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                onClick={() => handleBlogClick(blog.slug)}
              >
                <div className="image-container">
                  <div className="overlay-content">
                    <h2>{blog.title}</h2>
                    <div className="caption">{blog.caption}</div>
                  </div>
                </div>
                <div className="content">
                  <p>{blog.excerpt}</p>
                  <div className="meta">{blog.date} • {blog.readTime}</div>
                </div>
              </BlogCard>
            ))}
          </BlogGrid>
        ) : (
          <BlogHeader>
            <h2>Coming Soon</h2>
            <p>We're working on bringing you insightful content about AI in education. Stay tuned!</p>
          </BlogHeader>
        )}
      </BlogContainer>
    </PageWrapper>
  );
};

export default BlogPage;